
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Clock, BarChart3, Settings, TrendingUp, Target, Play, CheckCircle, Database, Bot, FileText, Layers } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { getAuthUrl } from "@/utils/redirectUtils";

const FreelancerPage = () => {
  const handleStartFree = () => {
    window.location.href = getAuthUrl();
  };

  const painPoints = [
    {
      icon: <FileText className="h-8 w-8 text-orange-500" />,
      title: "Manual Reporting Takes 15+ Hours Weekly",
      description: "Creating custom reports for each client eats up billable hours that could be spent on optimization."
    },
    {
      icon: <Settings className="h-8 w-8 text-orange-500" />,
      title: "Tedious Account Setup & Campaign Structure", 
      description: "Building campaigns from scratch for each client is repetitive and time-consuming."
    },
    {
      icon: <Clock className="h-8 w-8 text-orange-500" />,
      title: "Time-Consuming Account Audits",
      description: "Manual keyword analysis, quality score reviews, and performance audits limit client capacity."
    }
  ];

  const features = [
    {
      icon: <FileText className="h-8 w-8 text-blue-500" />,
      title: "Automated Client Reporting",
      description: "White-label reports with custom branding. Automated weekly delivery with performance insights and recommendations."
    },
    {
      icon: <Database className="h-8 w-8 text-blue-500" />,
      title: "Google Ads API Integration",
      description: "Direct API connection for bulk operations, real-time data sync, and advanced campaign management across multiple accounts."
    },
    {
      icon: <Bot className="h-8 w-8 text-blue-500" />,
      title: "AI Keyword Research & Budget Optimization",
      description: "Advanced keyword analysis aligned with client budgets. Automated bid management and Quality Score optimization."
    },
    {
      icon: <Layers className="h-8 w-8 text-blue-500" />,
      title: "Multi-Client Management Dashboard",
      description: "Centralized interface for managing multiple client accounts with bulk editing, performance tracking, and alert systems."
    }
  ];

  const steps = [
    {
      number: "1",
      title: "Connect Client Accounts via API",
      description: "Secure OAuth integration with multiple Google Ads accounts. Bulk import existing campaigns and account structures."
    },
    {
      number: "2", 
      title: "AI Analyzes & Optimizes Everything",
      description: "Automated keyword research, budget allocation analysis, Quality Score audits, and campaign structure optimization aligned with client budgets."
    },
    {
      number: "3",
      title: "Automated Reporting & Management",
      description: "Weekly white-label reports, ongoing optimization alerts, performance tracking, and automated client communications."
    }
  ];

  const benefits = [
    "80% reduction in reporting time",
    "Manage 3x more clients efficiently", 
    "Automated Google Ads API operations",
    "White-label reporting with your branding",
    "Advanced keyword research & budget optimization",
    "Real-time performance tracking & alerts"
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Google Ads Management for Freelancers | Upfixe</title>
        <meta name="description" content="Deliver agency-quality Google Ads campaigns as a solo freelancer. Automate reporting, manage multiple clients, scale your business. Free to start." />
        <meta name="keywords" content="Google Ads for freelancers, freelance Google Ads consultant, white-label Google Ads reporting" />
        <meta property="og:title" content="Google Ads Management for Freelancers | Upfixe" />
        <meta property="og:description" content="Deliver agency-quality Google Ads campaigns as a solo freelancer. Automate reporting, manage multiple clients, scale your business." />
        <link rel="canonical" href="https://upfixe.com/freelancer" />
      </Helmet>
      <Header />
      
      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">
          {/* Hero Section */}
          <div className="text-center mb-20">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Automate Your Busywork, <br />
              <span className="text-blue-600">Scale Your Clients.</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Professional Google Ads management platform for consultants. Automate client reporting, streamline account setup, and use AI with Google Ads API for keyword research aligned with client budgets.
            </p>
            <div className="flex justify-center mb-6">
              <Button 
                size="lg" 
                className="text-lg px-8 h-14 bg-blue-600 hover:bg-blue-700 font-semibold"
                onClick={handleStartFree}
              >
                Get Free Account
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">No credit card required • Google Ads API integration • Professional features</p>
          </div>

          {/* Problem Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Expert Google Ads Consultant?</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Stop wasting time on manual tasks. Scale your client base with professional automation tools.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {painPoints.map((point, index) => (
                <Card key={index} className="text-center p-6 border-orange-200 bg-orange-50/50">
                  <div className="flex justify-center mb-4">{point.icon}</div>
                  <h3 className="text-xl font-semibold mb-3">{point.title}</h3>
                  <p className="text-muted-foreground">{point.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Solution Section */}
          <div className="mb-20 bg-blue-50 p-12 rounded-2xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-6">Professional-Grade Google Ads Management Platform</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                Automate client reporting, streamline account setup with Google Ads API integration, and use AI for keyword research aligned with client budgets. Built specifically for Google Ads consultants.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Video Demo Section */}
          <section className="py-24 px-4 bg-gradient-to-br from-background to-muted/50 mb-20">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  See How Google Ads Freelancers Scale Their Business
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Watch how Google Ads consultants automate client reporting, streamline account management, 
                  and scale their business with professional-grade automation tools.
                </p>
              </div>

              <div className="relative max-w-4xl mx-auto mb-12">
                <div className="aspect-video rounded-2xl overflow-hidden border-2 border-primary/20">
                  <iframe
                    src="https://www.youtube.com/embed/ywhSSihPN_E"
                    title="Upfixe Freelancer Demo Video"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  onClick={handleStartFree}
                  className="text-lg px-8 bg-blue-600 hover:bg-blue-700"
                >
                  Get Free Account
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="text-lg px-8"
                  onClick={() => window.open('https://youtu.be/ywhSSihPN_E', '_blank')}
                >
                  <Play className="mr-2 h-5 w-5" />
                  Watch Full Demo
                </Button>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Built for Google Ads Professionals</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Advanced automation tools that scale with your consulting business. API-powered features for serious practitioners.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Professional Google Ads Automation</h2>
              <p className="text-xl text-muted-foreground">
                From API integration to automated reporting in minutes.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              {steps.map((step, index) => (
                <div key={index} className={`flex flex-col md:flex-row gap-6 items-center mb-12 ${index === steps.length - 1 ? 'mb-0' : ''}`}>
                  <div className="flex-shrink-0 w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-2xl font-bold">
                    {step.number}
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="text-2xl font-semibold mb-3">{step.title}</h3>
                    <p className="text-lg text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Blog Links Section */}
          <div className="py-20 px-4 bg-blue-50 rounded-2xl mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Scale Your Consulting Practice</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Learn strategies to optimize client campaigns, reduce churn, and scale without adding staff overhead.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <a href="/blog/reduce-google-ads-cpa" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Reduce Google Ads CPA</h3>
                <p className="text-sm text-muted-foreground mb-4">7 proven strategies to help your clients cut cost-per-acquisition in half.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article â†’</span>
              </a>
              <a href="/blog/budget-optimization-guide" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Budget Optimization Guide</h3>
                <p className="text-sm text-muted-foreground mb-4">Complete framework for maximizing ROI on client campaigns.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article â†’</span>
              </a>
              <a href="/blog/google-ads-for-agencies" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Google Ads for Agencies</h3>
                <p className="text-sm text-muted-foreground mb-4">Scale your client management 10x with intelligent automation.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article â†’</span>
              </a>
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-12 rounded-2xl">
            <h2 className="text-4xl font-bold mb-4">Ready to Scale Your Google Ads Consulting?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Join hundreds of Google Ads consultants who've automated their busywork and scaled their client base with professional-grade tools.
            </p>
            <div className="flex justify-center mb-6">
              <Button 
                size="lg" 
                className="text-lg px-8 h-14 bg-white text-blue-600 hover:bg-gray-100 font-semibold"
                onClick={handleStartFree}
              >
                Get Free Account
              </Button>
            </div>
            <p className="text-sm opacity-80">Professional features • Google Ads API access • No credit card required</p>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default FreelancerPage;
